import { useMemo } from 'react';
import { BarChart3, Flame, Target, TrendingUp, BookOpen, Calendar, Trophy, ArrowLeft } from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Famous novel word counts for milestone comparisons
// ---------------------------------------------------------------------------
const MILESTONES = [
    { title: 'The Old Man and the Sea', author: 'Hemingway', words: 27_000 },
    { title: 'Of Mice and Men', author: 'Steinbeck', words: 30_000 },
    { title: 'The Great Gatsby', author: 'Fitzgerald', words: 47_094 },
    { title: 'Slaughterhouse-Five', author: 'Vonnegut', words: 49_459 },
    { title: 'Animal Farm', author: 'Orwell', words: 29_966 },
    { title: 'The Hitchhiker\'s Guide', author: 'Adams', words: 46_333 },
    { title: 'Fight Club', author: 'Palahniuk', words: 49_000 },
    { title: 'The Catcher in the Rye', author: 'Salinger', words: 73_404 },
    { title: '1984', author: 'Orwell', words: 88_942 },
    { title: 'To Kill a Mockingbird', author: 'Lee', words: 100_388 },
    { title: 'Harry Potter & the Sorcerer\'s Stone', author: 'Rowling', words: 77_325 },
    { title: 'The Da Vinci Code', author: 'Brown', words: 138_000 },
    { title: 'Gone Girl', author: 'Flynn', words: 145_719 },
    { title: 'Gone With the Wind', author: 'Mitchell', words: 418_053 },
    { title: 'War and Peace', author: 'Tolstoy', words: 580_000 },
];

function getMilestones(wordCount: number) {
    const passed = MILESTONES.filter(m => wordCount >= m.words).slice(-1)[0];
    const next = MILESTONES.find(m => m.words > wordCount);
    return { passed, next };
}

function getWeekDates(): string[] {
    const dates: string[] = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
}

// ---------------------------------------------------------------------------
// Stat Card component
// ---------------------------------------------------------------------------
function StatCard({
    icon: Icon,
    label,
    value,
    sub,
    accent,
    large = false,
}: {
    icon: typeof BarChart3;
    label: string;
    value: string | number;
    sub?: string;
    accent: string;
    large?: boolean;
}) {
    const { themeProfile } = useAppStore();
    return (
        <div
            className="rounded-2xl p-5 flex flex-col gap-2"
            style={{ backgroundColor: themeProfile.paperSurface, border: `1px solid ${themeProfile.border}` }}
        >
            <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}20` }}>
                    <Icon className="w-4 h-4" style={{ color: accent }} />
                </div>
                <span className="text-xs font-medium" style={{ color: themeProfile.text, opacity: 0.55 }}>{label}</span>
            </div>
            <p className={cn('font-bold font-literary', large ? 'text-4xl' : 'text-2xl')} style={{ color: themeProfile.text }}>
                {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {sub && <p className="text-xs" style={{ color: themeProfile.text, opacity: 0.45 }}>{sub}</p>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Weekly Bar Chart
// ---------------------------------------------------------------------------
function WeeklyChart({ dailyWordCounts, accent }: { dailyWordCounts: Record<string, number>; accent: string }) {
    const { themeProfile } = useAppStore();
    const weekDates = getWeekDates();
    const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const values = weekDates.map(d => dailyWordCounts[d] ?? 0);
    const maxVal = Math.max(...values, 1);
    const today = new Date().toISOString().split('T')[0];

    return (
        <div className="rounded-2xl p-5" style={{ backgroundColor: themeProfile.paperSurface, border: `1px solid ${themeProfile.border}` }}>
            <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}20` }}>
                    <Calendar className="w-4 h-4" style={{ color: accent }} />
                </div>
                <span className="text-xs font-medium" style={{ color: themeProfile.text, opacity: 0.55 }}>This Week</span>
            </div>
            <div className="flex items-end gap-2 h-28">
                {weekDates.map((date, i) => {
                    const val = values[i];
                    const pct = (val / maxVal) * 100;
                    const isToday = date === today;
                    const dayLabel = DAY_LABELS[new Date(date + 'T12:00:00').getDay()];
                    return (
                        <div key={date} className="flex-1 flex flex-col items-center gap-1.5">
                            <div className="w-full flex flex-col justify-end" style={{ height: '80px' }}>
                                <div
                                    className="w-full rounded-t-md transition-all duration-500"
                                    style={{
                                        height: val > 0 ? `${Math.max(pct, 4)}%` : '4px',
                                        backgroundColor: isToday ? accent : `${accent}55`,
                                        minHeight: val > 0 ? '8px' : '4px',
                                    }}
                                    title={`${val.toLocaleString()} words`}
                                />
                            </div>
                            <span className="text-[10px]" style={{ color: isToday ? accent : themeProfile.text, opacity: isToday ? 1 : 0.4, fontWeight: isToday ? 600 : 400 }}>
                                {dayLabel}
                            </span>
                            {val > 0 && (
                                <span className="text-[9px]" style={{ color: themeProfile.text, opacity: 0.35 }}>
                                    {val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Milestone comparison banner
// ---------------------------------------------------------------------------
function MilestoneBanner({ totalWords, accent }: { totalWords: number; accent: string }) {
    const { themeProfile } = useAppStore();
    const { passed, next } = getMilestones(totalWords);

    if (totalWords === 0) return null;

    return (
        <div
            className="rounded-2xl p-5 col-span-full"
            style={{ backgroundColor: `${accent}12`, border: `1px dashed ${accent}50` }}
        >
            <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4" style={{ color: accent }} />
                <span className="text-xs font-semibold" style={{ color: accent }}>Novel Milestones</span>
            </div>
            <div className="flex flex-wrap gap-6">
                {passed && (
                    <div>
                        <p className="text-sm font-semibold" style={{ color: themeProfile.text }}>
                            ✅ Longer than <em>{passed.title}</em>
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: themeProfile.text, opacity: 0.5 }}>
                            {passed.author} · {passed.words.toLocaleString()} words
                        </p>
                    </div>
                )}
                {next && (
                    <div>
                        <p className="text-sm font-semibold" style={{ color: themeProfile.text }}>
                            🎯 {(next.words - totalWords).toLocaleString()} words until <em>{next.title}</em>
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: themeProfile.text, opacity: 0.5 }}>
                            {next.author} · {next.words.toLocaleString()} words
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Novel progress ring
// ---------------------------------------------------------------------------
function ProgressRing({ pct, accent, size = 96 }: { pct: number; accent: string; size?: number }) {
    const r = (size / 2) - 10;
    const circumference = 2 * Math.PI * r;
    const strokeDash = (pct / 100) * circumference;
    return (
        <svg width={size} height={size} className="-rotate-90">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`${accent}22`} strokeWidth={8} />
            <circle
                cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke={accent} strokeWidth={8}
                strokeDasharray={`${strokeDash} ${circumference}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 0.8s ease' }}
            />
        </svg>
    );
}

// ---------------------------------------------------------------------------
// Main View
// ---------------------------------------------------------------------------
export function WritingStatsView() {
    const {
        novels,
        activeNovelId,
        dailyWordCounts,
        streak,
        sessions,
        themeProfile,
        setViewMode,
    } = useAppStore();

    const accent = themeProfile.accent;
    const today = new Date().toISOString().split('T')[0];

    const activeNovel = novels.find(n => n.id === activeNovelId);
    const totalWords = useMemo(
        () => novels.reduce((sum, n) => sum + n.chapters.reduce((s, c) => s + c.wordCount, 0), 0),
        [novels]
    );
    const activeNovelWords = useMemo(
        () => activeNovel?.chapters.reduce((s, c) => s + c.wordCount, 0) ?? 0,
        [activeNovel]
    );
    const todayWords = dailyWordCounts[today] ?? 0;
    const weekDates = getWeekDates();
    const weekWords = weekDates.reduce((sum, d) => sum + (dailyWordCounts[d] ?? 0), 0);

    const totalSessions = sessions.filter(s => s.endTime !== null).length;
    const avgSessionWords = totalSessions > 0
        ? Math.round(totalWords / totalSessions)
        : 0;

    const targetWords = activeNovel?.targetWordCount ?? 80_000;
    const progressPct = targetWords > 0 ? Math.min(100, Math.round((activeNovelWords / targetWords) * 100)) : 0;

    return (
        <div className="h-full overflow-auto" style={{ backgroundColor: themeProfile.background }}>
            {/* Header */}
            <header
                className="sticky top-0 z-10 px-6 md:px-8 py-5 flex items-center justify-between"
                style={{ backgroundColor: themeProfile.background, borderBottom: `1px solid ${themeProfile.border}` }}
            >
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setViewMode('manuscript')}
                        className="p-2 rounded-xl transition-colors hover:opacity-70"
                        style={{ color: themeProfile.text }}
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}20` }}>
                        <BarChart3 className="w-5 h-5" style={{ color: accent }} />
                    </div>
                    <div>
                        <h1 className="text-xl font-literary font-semibold" style={{ color: themeProfile.text }}>Writing Stats</h1>
                        <p className="text-xs" style={{ color: themeProfile.text, opacity: 0.45 }}>Your story in numbers</p>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 md:px-8 py-8 space-y-8">

                {/* ── Today strip ── */}
                <section>
                    <h2 className="text-sm font-semibold mb-3" style={{ color: themeProfile.text, opacity: 0.5 }}>TODAY</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatCard icon={TrendingUp} label="Words Today" value={todayWords} sub="words written" accent={accent} large />
                        <StatCard icon={Flame} label="Current Streak" value={`${streak.currentStreak}d`} sub={`${streak.totalDaysWritten} total days written`} accent="#E8875A" />
                        <StatCard icon={BookOpen} label="Active Novel" value={activeNovelWords.toLocaleString()} sub={activeNovel?.title ?? 'No novel selected'} accent={accent} />
                        <StatCard icon={Target} label="Novel Goal" value={`${progressPct}%`} sub={`${(targetWords - activeNovelWords).toLocaleString()} words to go`} accent="#7B9E87" />
                    </div>
                </section>

                {/* ── Novel Progress ring ── */}
                {activeNovel && (
                    <section
                        className="rounded-2xl p-6 flex items-center gap-6"
                        style={{ backgroundColor: themeProfile.paperSurface, border: `1px solid ${themeProfile.border}` }}
                    >
                        <div className="relative flex-shrink-0">
                            <ProgressRing pct={progressPct} accent={accent} size={100} />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-lg font-bold" style={{ color: themeProfile.text }}>{progressPct}%</span>
                            </div>
                        </div>
                        <div className="flex-1">
                            <p className="font-semibold text-base" style={{ color: themeProfile.text }}>{activeNovel.title}</p>
                            <p className="text-sm mt-1" style={{ color: themeProfile.text, opacity: 0.5 }}>
                                {activeNovelWords.toLocaleString()} / {targetWords.toLocaleString()} words
                            </p>
                            <div className="mt-3 w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: `${accent}20` }}>
                                <div
                                    className="h-full rounded-full transition-all duration-700"
                                    style={{ width: `${progressPct}%`, backgroundColor: accent }}
                                />
                            </div>
                            <p className="text-xs mt-2" style={{ color: themeProfile.text, opacity: 0.4 }}>
                                Standard novel target: 80,000 words · Set your target in Settings
                            </p>
                        </div>
                    </section>
                )}

                {/* ── Weekly chart ── */}
                <section>
                    <h2 className="text-sm font-semibold mb-3" style={{ color: themeProfile.text, opacity: 0.5 }}>LAST 7 DAYS</h2>
                    <WeeklyChart dailyWordCounts={dailyWordCounts} accent={accent} />
                </section>

                {/* ── All time stats ── */}
                <section>
                    <h2 className="text-sm font-semibold mb-3" style={{ color: themeProfile.text, opacity: 0.5 }}>ALL TIME</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatCard icon={BookOpen} label="Total Words" value={totalWords} sub="across all novels" accent={accent} />
                        <StatCard icon={TrendingUp} label="This Week" value={weekWords} sub="words written" accent="#5B7FA6" />
                        <StatCard icon={Calendar} label="Sessions" value={totalSessions} sub="writing sessions" accent="#9B6E9E" />
                        <StatCard icon={Trophy} label="Avg Session" value={avgSessionWords.toLocaleString()} sub="words per session" accent="#E8C45A" />
                    </div>
                </section>

                {/* ── Milestone banner ── */}
                <section>
                    <h2 className="text-sm font-semibold mb-3" style={{ color: themeProfile.text, opacity: 0.5 }}>MILESTONES</h2>
                    <MilestoneBanner totalWords={activeNovelWords} accent={accent} />
                    {activeNovelWords === 0 && (
                        <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: themeProfile.paperSurface, border: `1px solid ${themeProfile.border}` }}>
                            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: accent }} />
                            <p className="text-sm font-medium" style={{ color: themeProfile.text, opacity: 0.5 }}>
                                Write your first words to unlock milestone comparisons<br />
                                <span className="text-xs">e.g. "You've written more than The Great Gatsby!"</span>
                            </p>
                        </div>
                    )}
                </section>

                {/* ── Bottom padding ── */}
                <div className="h-8" />
            </main>
        </div>
    );
}
